/** Trims a base path to a comparable form: "", "/", "/dev/" → "/" or "/dev". */
export const normalizeBasePath = (value: string | undefined): string => {
	const trimmed = (value ?? "").trim().replace(/^\/+|\/+$/g, "");
	return trimmed ? `/${trimmed}` : "/";
};

/**
 * `VITE_BASE_PATH` is a **build-time** variable: Vite bakes it into every asset
 * URL in the prerendered shell, and the router's basepath has to match it. The
 * container publishes it into `window._env_` alongside the API URLs, which are
 * genuinely runtime-configurable — so setting it there looks like it should
 * work, and silently does nothing. A deployment that does it mounts the app at
 * one path while the bundle points at another, and every route 404s.
 *
 * Returns an actionable message on that mismatch, or null when the deployment
 * is consistent (or says nothing about the base path at all).
 */
export const describeBasePathMismatch = (
	builtBasePath: string | undefined,
	deployedBasePath: string | undefined,
): string | null => {
	if (!deployedBasePath?.trim()) return null;

	const built = normalizeBasePath(builtBasePath);
	const deployed = normalizeBasePath(deployedBasePath);

	if (built === deployed) return null;

	return (
		`This build is served from "${built}" but the container sets ` +
		`VITE_BASE_PATH="${deployed}". VITE_BASE_PATH is baked in at build time, ` +
		"so setting it at runtime has no effect — rebuild the image with " +
		`--build-arg VITE_BASE_PATH=${deployed} (or drop the runtime value).`
	);
};
